import React, { useState } from 'react';
import { RFQCreate } from '../types';

interface AddRFQFormProps {
    onSubmit: (rfq: RFQCreate) => Promise<void>;
    onCancel?: () => void;
}

const AddRFQForm: React.FC<AddRFQFormProps> = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState<RFQCreate>({
        title: '',
        description: '',
        client_name: '',
        part_type: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!formData.title.trim() || !formData.client_name.trim()) {
            setError('Title and Client Name are required');
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit({
                title: formData.title.trim(),
                description: formData.description?.trim() || undefined,
                client_name: formData.client_name.trim(),
                part_type: formData.part_type?.trim() || undefined,
            });
            setFormData({
                title: '',
                description: '',
                client_name: '',
                part_type: '',
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create RFQ');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <article className="relative bg-white border border-[#141414]/20 shadow-[0_1px_0_0_#141414]">
            <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
            <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
            <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
            <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />

            <div className="px-6 py-4 border-b border-[#141414]/15">
                <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                    New request
                </div>
                <h2 className="ff-serif text-3xl leading-none mt-1">Add RFQ</h2>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5">
                {error && (
                    <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-3 py-2 ff-mono text-xs">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label
                            className="block ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-1"
                            htmlFor="title"
                        >
                            Title <span className="text-[#e0461e]">*</span>
                        </label>
                        <input
                            id="title"
                            name="title"
                            type="text"
                            placeholder="Enter RFQ title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                        />
                    </div>

                    <div>
                        <label
                            className="block ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-1"
                            htmlFor="client_name"
                        >
                            Client name <span className="text-[#e0461e]">*</span>
                        </label>
                        <input
                            id="client_name"
                            name="client_name"
                            type="text"
                            placeholder="Enter client name"
                            value={formData.client_name}
                            onChange={handleChange}
                            required
                            className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <label
                        className="block ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-1"
                        htmlFor="part_type"
                    >
                        Part type
                    </label>
                    <input
                        id="part_type"
                        name="part_type"
                        type="text"
                        placeholder="Enter robotics part type (optional)"
                        value={formData.part_type}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                    />
                </div>

                <div className="mb-6">
                    <label
                        className="block ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-1"
                        htmlFor="description"
                    >
                        Description
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        rows={4}
                        placeholder="Enter RFQ description (optional)"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm resize-y"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                    >
                        {isSubmitting ? 'Creating…' : 'Create RFQ'}
                    </button>
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>
        </article>
    );
};

export default AddRFQForm;
